/*
 * ******************************************************
 * Copyright (c) 2022 Jenke & Rauschert GbR <info@aiandor.de> - All Rights Reserved
 *
 * This file is part of a software product owned by the Jenke & Rauschert GbR.
 * Unauthorized distribution of this file is strictly prohibited.
 * ******************************************************
 */

import {Alert, Breadcrumb, Descriptions, Card, Result, Tag, Tooltip} from "antd";
import {Link, useParams} from "react-router-dom";
import {User} from "../core/datamodel";
import {get} from "../core/request";
import {useEffect, useState} from "react";
import {useAppDispatch, useAppSelector} from "../store";
import {ViewState} from "../states";
import {openDrawer} from "../reducers/drawers-reducer";
import {CustomButton} from "../components/buttons";
import {IconSet} from "../components/icons";
import Loading from "../components/loading";

export default function UserDetailsView(props: any) {
    const params: { id: string } = useParams();
    const reloadSignal = useAppSelector(state => state.reloadSignals.user)
    const [user, setUser] = useState({} as User);
    const [state, setState] = useState(ViewState.loading);

    useEffect(() => {
        get("users/" + params.id).done((response) => {
                setUser(new User(response.data.user));
                setState(ViewState.success);
            }
        ).fail(() => setState(ViewState.failed))
    }, [params.id, reloadSignal])

    let dispatch = useAppDispatch();

    const onEdit = () => dispatch(openDrawer({key: 'user', object: user}));


    const breadcrumb = (
        <Breadcrumb style={{paddingTop: 6, paddingBottom: 6}}>
            <Breadcrumb.Item><Link to="/">Home</Link></Breadcrumb.Item>
            <Breadcrumb.Item><Link to="/users">Nutzer</Link></Breadcrumb.Item>
            <Breadcrumb.Item>{!!user?.props?.username ? user.props.username : "Unknown"}</Breadcrumb.Item>
        </Breadcrumb>
    )

    const pageHeader = (
        <Card
            title={user?.props?.username}
            extra={
                <CustomButton mode={'edit'} onClick={onEdit} tooltip={"User bearbeiten"}/>
            }>
            <Descriptions colon={false} size="small">
                <Descriptions.Item
                    label={
                        <Tooltip overlay={"Kontakt"}>
                            <IconSet type='persons' style={{lineHeight: '24px', alignSelf: 'center'}}/>
                        </Tooltip>}
                    children={user?.props?.person ?
                        <Tooltip overlay={"Kontakt"}>
                            <Link to={"/persons/" + user.props.person.props.uuid}>
                                {user.props.person.props.repr}
                            </Link>
                        </Tooltip> : ""}
                />

                <Descriptions.Item
                    label={
                        <Tooltip overlay={"Gruppen"}>
                            <IconSet type='groups' style={{lineHeight: '24px', alignSelf: 'center'}}/>
                        </Tooltip>}
                    children={
                        <Tooltip overlay={"Gruppen"}>
                            {
                                user?.props?.groups?.map(group => <Tag children={group}/>)
                            }
                        </Tooltip>
                    }
                />
            </Descriptions>
        </Card>
    )

    const disabledAlert = (
        !!user?.props?.disabled ? (<Alert
            message="Dieser Nutzer ist deaktiviert!"
            description="Zur weiteren Verwendnung bearbeiten und reaktivieren."
            type="warning"
            showIcon={true}
            style={{marginBottom: 24}}
        />) : (<></>)
    )

    switch (state) {
        case ViewState.loading:
            return (
                <>
                    {breadcrumb}
                    <Loading/>
                </>
            )
        case ViewState.success:
            return (
                <>
                    {breadcrumb}
                    {disabledAlert}
                    {pageHeader}
                </>
            )

        case ViewState.failed:
        default:
            return (
                <>
                    {breadcrumb}

                    <Result
                        status="404"
                        title="Nicht gefunden"
                        subTitle="Entschuldigung, die gesuchte Person konnte nicht gefunden werden!"
                    />
                </>
            )
    }
}